import {useId} from "react";
import {css, cx} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {RP_RESERVATION_TEST_IDS} from "@pg-mono/test-ids";

import {getPropertyNoun} from "../../offer/utils/get_property_noun";
import {RESERVATION_CODE_LENGTH} from "../constants/reservation_constants";
import {
    RESERVATION_CODE_STEP_TITLE,
    RESERVATION_DATA_ADMINISTRATOR_TEXT,
    RESERVATION_NEW_CODE_SENT_TEXT,
    RESERVATION_RESEND_BLOCKED_COPY,
    RESERVATION_RESEND_COOLDOWN_COPY,
    RESERVATION_RESEND_EXHAUSTED_TEXT,
    RESERVATION_RESEND_LINK_TEXT,
    RESERVATION_RESEND_QUESTION,
    RESERVATION_SENDING_CODE_LABEL,
    RESERVATION_TERMS_LINK_TEXT
} from "../constants/reservation_copy";
import {IReservationCodeStepContentProps} from "../types/IReservationCodeStepContentProps";
import {IReservationResendView} from "../types/IReservationResendView";
import {getReservationCodeStepContentView} from "../utils/get_reservation_code_step_view";
import {reservationFocusRingStyle, reservationStepContentStyle, reservationStepTextStyle} from "./reservation_step_styles";
import {renderCountdown, renderUntranslatedSuffix, renderWithHotlineLink} from "./reservation_text";
import {ReservationCodeInputs} from "./ReservationCodeInputs";
import {ReservationFailedNotice} from "./ReservationFailedNotice";
import {ReservationNameStepContent} from "./ReservationNameStepContent";

interface IProps extends IReservationCodeStepContentProps {
    className?: string;
}

// Header, back and close controls belong to the host modal - the content renders its own paddings only
export const ReservationCodeStepContent = (props: IProps) => {
    const {
        titleId,
        descriptionId,
        propertyNumber,
        offerType,
        vendorName,
        maskedEmail,
        digits,
        focusRequestId,
        isNewCodeSent,
        isSubmitting,
        isExpired,
        isLocked,
        hasError,
        errorMessage,
        retrySecondsLeft,
        resend,
        onDigitsChange,
        onComplete,
        onResend,
        onRetry,
        onClose,
        className
    } = props;

    const errorId = useId();
    const view = getReservationCodeStepContentView(props);

    if (view.kind === "loading") {
        return (
            <div role="status" className={cx(reservationStepContentStyle, initiatingStyle, className)} data-testid={RP_RESERVATION_TEST_IDS.LOADER}>
                <span className={srOnlyStyle}>{RESERVATION_SENDING_CODE_LABEL}</span>
                <Loader size="lg" fullWidth />
            </div>
        );
    }

    if (view.kind === "name") {
        return <ReservationNameStepContent titleId={titleId} descriptionId={descriptionId} nameForm={view.nameForm} className={className} />;
    }

    if (view.kind === "failed") {
        return (
            <ReservationFailedNotice
                titleId={titleId}
                descriptionId={descriptionId}
                error={view.error}
                retrySecondsLeft={retrySecondsLeft}
                onRetry={onRetry}
                onClose={onClose}
                className={className}
            />
        );
    }

    const noun = getPropertyNoun(offerType);

    return (
        <div className={cx(reservationStepContentStyle, className)} aria-busy={isSubmitting}>
            <div className={reservationStepTextStyle}>
                <Text as="h2" id={titleId} variant="headline_4" color="var(--colors-secondary)">
                    {RESERVATION_CODE_STEP_TITLE}
                </Text>

                <Text as="p" id={descriptionId} variant="body_copy_2" color="var(--colors-gray-800)">
                    Aby dokonać bezpłatnej i niezobowiązującej rezerwacji {noun.genitive}
                    {renderUntranslatedSuffix(propertyNumber)}, wpisz {RESERVATION_CODE_LENGTH}-cyfrowy kod, który wysłaliśmy{" "}
                    {maskedEmail ? (
                        <>
                            na adres: <strong className="bt">{maskedEmail}</strong>
                        </>
                    ) : (
                        "na Twój adres e-mail"
                    )}
                    .
                </Text>
            </div>

            <div className={codeStyle}>
                <ReservationCodeInputs
                    digits={digits}
                    titleId={titleId}
                    describedById={errorMessage ? errorId : undefined}
                    hasError={hasError}
                    disabled={isSubmitting || isExpired || isLocked}
                    focusRequestId={focusRequestId}
                    onChange={onDigitsChange}
                    onComplete={onComplete}
                />

                {errorMessage ? (
                    <p role="alert" id={errorId} className={errorTextStyle} data-testid={RP_RESERVATION_TEST_IDS.CODE_ERROR}>
                        {renderWithHotlineLink(errorMessage)}
                    </p>
                ) : null}

                {isSubmitting ? (
                    <div className={loaderOverlayStyle} data-testid={RP_RESERVATION_TEST_IDS.LOADER}>
                        <Loader size="lg" />
                    </div>
                ) : null}
            </div>

            <div className={resendStyle}>
                <ReservationResendRow resend={resend} isSubmitting={isSubmitting} onResend={onResend} />
            </div>

            <p role="status" className={srOnlyStyle}>
                {isNewCodeSent ? RESERVATION_NEW_CODE_SENT_TEXT : null}
            </p>

            {/* the confirmed reservation is sent to the developer as an application, so the data transfer is disclosed before the code is entered */}
            <Text as="p" variant="info_txt_1" color="var(--colors-gray-700)" className={clauseStyle}>
                Wraz ze zgłoszeniem prześlemy Twoje dane kontaktowe do dewelopera{renderUntranslatedSuffix(vendorName)}, który porozmawia z Tobą bez zobowiązań.{" "}
                {RESERVATION_DATA_ADMINISTRATOR_TEXT}{" "}
                <a href={rpAppLink.termsOfService.base()} target="_blank" rel="noopener" className={cx(termsLinkStyle, reservationFocusRingStyle)}>
                    {RESERVATION_TERMS_LINK_TEXT}
                </a>
                .
            </Text>
        </div>
    );
};

interface IReservationResendRowProps {
    resend: IReservationResendView;
    isSubmitting: boolean;
    onResend: () => void;
}

const ReservationResendRow = (props: IReservationResendRowProps) => {
    const {resend, isSubmitting, onResend} = props;

    if (resend.state === "exhausted") {
        return (
            <Text as="p" variant="info_txt_1" align="center" color="var(--colors-gray-700)" dataTestId={RP_RESERVATION_TEST_IDS.RESEND_EXHAUSTED}>
                {renderWithHotlineLink(RESERVATION_RESEND_EXHAUSTED_TEXT)}
            </Text>
        );
    }

    // a timer is not a live region - announcing every second would drown out the rest of the dialog
    if (resend.secondsLeft > 0 && (resend.state === "cooldown" || resend.state === "blocked")) {
        return (
            <Text as="p" variant="info_txt_1" align="center" color="var(--colors-gray-700)" dataTestId={RP_RESERVATION_TEST_IDS.RESEND_COOLDOWN}>
                <span role="timer">
                    {renderCountdown(resend.state === "cooldown" ? RESERVATION_RESEND_COOLDOWN_COPY : RESERVATION_RESEND_BLOCKED_COPY, resend.secondsLeft)}
                </span>
            </Text>
        );
    }

    return (
        <Text as="p" variant="info_txt_1" align="center" color="var(--colors-gray-700)">
            {RESERVATION_RESEND_QUESTION}{" "}
            <button
                type="button"
                className={cx(resendButtonStyle, reservationFocusRingStyle)}
                disabled={resend.state !== "available" || isSubmitting}
                onClick={onResend}
                data-testid={RP_RESERVATION_TEST_IDS.RESEND_BUTTON}
            >
                {RESERVATION_RESEND_LINK_TEXT}
            </button>
        </Text>
    );
};

const initiatingStyle = css({
    alignItems: "center",
    justifyContent: "center",
    minHeight: "20rem"
});

const srOnlyStyle = css({
    srOnly: true
});

const codeStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    rowGap: 1
});

const errorTextStyle = css({
    m: 0,
    color: "var(--colors-danger)",
    fontSize: "1.2rem",
    lineHeight: "1.6rem",
    md: {
        fontSize: "1.4rem",
        lineHeight: "2rem"
    }
});

const loaderOverlayStyle = css({
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgb(from var(--colors-white) r g b / 0.7)"
});

const resendStyle = css({
    pt: 1
});

const resendButtonStyle = css({
    display: "inline",
    p: 0,
    border: "none",
    backgroundColor: "transparent",
    color: "inherit",
    font: "inherit",
    textDecoration: "underline",
    cursor: "pointer",
    _disabled: {
        cursor: "default",
        opacity: 0.6
    }
});

const clauseStyle = css({
    fontSize: "1.067rem",
    lineHeight: "1.2rem"
});

const termsLinkStyle = css({
    color: "var(--colors-secondary)",
    textDecoration: "underline"
});
import {useId} from "react";
import {css, cx} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {RP_RESERVATION_TEST_IDS} from "@pg-mono/test-ids";

import {getPropertyNoun} from "../../offer/utils/get_property_noun";
import {RESERVATION_CODE_LENGTH} from "../constants/reservation_constants";
import {
    RESERVATION_CODE_STEP_TITLE,
    RESERVATION_DATA_ADMINISTRATOR_TEXT,
    RESERVATION_NEW_CODE_SENT_TEXT,
    RESERVATION_RESEND_BLOCKED_COPY,
    RESERVATION_RESEND_COOLDOWN_COPY,
    RESERVATION_RESEND_EXHAUSTED_TEXT,
    RESERVATION_RESEND_LINK_TEXT,
    RESERVATION_RESEND_QUESTION,
    RESERVATION_SENDING_CODE_LABEL,
    RESERVATION_TERMS_LINK_TEXT
} from "../constants/reservation_copy";
import {IReservationCodeStepContentProps} from "../types/IReservationCodeStepContentProps";
import {IReservationResendView} from "../types/IReservationResendView";
import {getReservationCodeStepContentView} from "../utils/get_reservation_code_step_view";
import {reservationFocusRingStyle, reservationStepContentStyle, reservationStepTextStyle} from "./reservation_step_styles";
import {renderCountdown, renderUntranslatedSuffix, renderWithHotlineLink} from "./reservation_text";
import {ReservationCodeInputs} from "./ReservationCodeInputs";
import {ReservationFailedNotice} from "./ReservationFailedNotice";
import {ReservationNameStepContent} from "./ReservationNameStepContent";

interface IProps extends IReservationCodeStepContentProps {
    className?: string;
}

// Header, back and close controls belong to the host modal - the content renders its own paddings only
export const ReservationCodeStepContent = (props: IProps) => {
    const {
        titleId,
        descriptionId,
        propertyNumber,
        offerType,
        vendorName,
        maskedEmail,
        digits,
        focusRequestId,
        isNewCodeSent,
        isSubmitting,
        isExpired,
        isLocked,
        hasError,
        errorMessage,
        retrySecondsLeft,
        resend,
        onDigitsChange,
        onComplete,
        onResend,
        onRetry,
        onClose,
        className
    } = props;

    const errorId = useId();
    const view = getReservationCodeStepContentView(props);

    if (view.kind === "loading") {
        return (
            <div role="status" className={cx(reservationStepContentStyle, initiatingStyle, className)} data-testid={RP_RESERVATION_TEST_IDS.LOADER}>
                <span className={srOnlyStyle}>{RESERVATION_SENDING_CODE_LABEL}</span>
                <Loader size="lg" fullWidth />
            </div>
        );
    }

    if (view.kind === "name") {
        return <ReservationNameStepContent titleId={titleId} descriptionId={descriptionId} nameForm={view.nameForm} className={className} />;
    }

    if (view.kind === "failed") {
        return (
            <ReservationFailedNotice
                titleId={titleId}
                descriptionId={descriptionId}
                error={view.error}
                retrySecondsLeft={retrySecondsLeft}
                onRetry={onRetry}
                onClose={onClose}
                className={className}
            />
        );
    }

    const noun = getPropertyNoun(offerType);

    return (
        <div className={cx(reservationStepContentStyle, className)} aria-busy={isSubmitting}>
            <div className={reservationStepTextStyle}>
                <Text as="h2" id={titleId} variant="headline_4" color="var(--colors-secondary)">
                    {RESERVATION_CODE_STEP_TITLE}
                </Text>

                <Text as="p" id={descriptionId} variant="body_copy_2" color="var(--colors-gray-800)">
                    Aby dokonać bezpłatnej i niezobowiązującej rezerwacji {noun.genitive}
                    {renderUntranslatedSuffix(propertyNumber)}, wpisz {RESERVATION_CODE_LENGTH}-cyfrowy kod, który wysłaliśmy{" "}
                    {maskedEmail ? (
                        <>
                            na adres: <strong className="bt">{maskedEmail}</strong>
                        </>
                    ) : (
                        "na Twój adres e-mail"
                    )}
                    .
                </Text>
            </div>

            <div className={codeStyle}>
                <ReservationCodeInputs
                    digits={digits}
                    titleId={titleId}
                    describedById={errorMessage ? errorId : undefined}
                    hasError={hasError}
                    disabled={isSubmitting || isExpired || isLocked}
                    focusRequestId={focusRequestId}
                    onChange={onDigitsChange}
                    onComplete={onComplete}
                />

                {errorMessage ? (
                    <p role="alert" id={errorId} className={errorTextStyle} data-testid={RP_RESERVATION_TEST_IDS.CODE_ERROR}>
                        {renderWithHotlineLink(errorMessage)}
                    </p>
                ) : null}

                {isSubmitting ? (
                    <div className={loaderOverlayStyle} data-testid={RP_RESERVATION_TEST_IDS.LOADER}>
                        <Loader size="lg" />
                    </div>
                ) : null}
            </div>

            <div className={resendStyle}>
                <ReservationResendRow resend={resend} isSubmitting={isSubmitting} onResend={onResend} />
            </div>

            <p role="status" className={srOnlyStyle}>
                {isNewCodeSent ? RESERVATION_NEW_CODE_SENT_TEXT : null}
            </p>

            {/* the confirmed reservation is sent to the developer as an application, so the data transfer is disclosed before the code is entered */}
            <Text as="p" variant="info_txt_1" color="var(--colors-gray-700)" className={clauseStyle}>
                Wraz ze zgłoszeniem prześlemy Twoje dane kontaktowe do dewelopera{renderUntranslatedSuffix(vendorName)}, który porozmawia z Tobą bez zobowiązań.{" "}
                {RESERVATION_DATA_ADMINISTRATOR_TEXT}{" "}
                <a href={rpAppLink.termsOfService.base()} target="_blank" rel="noopener" className={cx(termsLinkStyle, reservationFocusRingStyle)}>
                    {RESERVATION_TERMS_LINK_TEXT}
                </a>
                .
            </Text>
        </div>
    );
};

interface IReservationResendRowProps {
    resend: IReservationResendView;
    isSubmitting: boolean;
    onResend: () => void;
}

const ReservationResendRow = (props: IReservationResendRowProps) => {
    const {resend, isSubmitting, onResend} = props;

    if (resend.state === "exhausted") {
        return (
            <Text as="p" variant="info_txt_1" align="center" color="var(--colors-gray-700)" dataTestId={RP_RESERVATION_TEST_IDS.RESEND_EXHAUSTED}>
                {renderWithHotlineLink(RESERVATION_RESEND_EXHAUSTED_TEXT)}
            </Text>
        );
    }

    // a timer is not a live region - announcing every second would drown out the rest of the dialog
    if (resend.secondsLeft > 0 && (resend.state === "cooldown" || resend.state === "blocked")) {
        return (
            <Text as="p" variant="info_txt_1" align="center" color="var(--colors-gray-700)" dataTestId={RP_RESERVATION_TEST_IDS.RESEND_COOLDOWN}>
                <span role="timer">
                    {renderCountdown(resend.state === "cooldown" ? RESERVATION_RESEND_COOLDOWN_COPY : RESERVATION_RESEND_BLOCKED_COPY, resend.secondsLeft)}
                </span>
            </Text>
        );
    }

    return (
        <Text as="p" variant="info_txt_1" align="center" color="var(--colors-gray-700)">
            {RESERVATION_RESEND_QUESTION}{" "}
            <button
                type="button"
                className={cx(resendButtonStyle, reservationFocusRingStyle)}
                disabled={resend.state !== "available" || isSubmitting}
                onClick={onResend}
                data-testid={RP_RESERVATION_TEST_IDS.RESEND_BUTTON}
            >
                {RESERVATION_RESEND_LINK_TEXT}
            </button>
        </Text>
    );
};

const initiatingStyle = css({
    alignItems: "center",
    justifyContent: "center",
    minHeight: "20rem"
});

const srOnlyStyle = css({
    srOnly: true
});

const codeStyle = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    rowGap: 1
});

const errorTextStyle = css({
    m: 0,
    color: "var(--colors-danger)",
    fontSize: "1.2rem",
    lineHeight: "1.6rem",
    md: {
        fontSize: "1.4rem",
        lineHeight: "2rem"
    }
});

const loaderOverlayStyle = css({
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgb(from var(--colors-white) r g b / 0.7)"
});

const resendStyle = css({
    pt: 1
});

const resendButtonStyle = css({
    display: "inline",
    p: 0,
    border: "none",
    backgroundColor: "transparent",
    color: "inherit",
    font: "inherit",
    textDecoration: "underline",
    cursor: "pointer",
    _disabled: {
        cursor: "default",
        opacity: 0.6
    }
});

const clauseStyle = css({
    fontSize: "1.067rem",
    lineHeight: "1.2rem"
});

const termsLinkStyle = css({
    color: "var(--colors-secondary)",
    textDecoration: "underline"
});
